import {
  getActiveBots,
  incrementBotUsage,
} from '@store/firebase/customBot/update-custom-bot';
import {
  ICustomBot,
  CUSTOM_BOT_TYPE,
  IBotExecutionResult,
} from '@typings/customBot/customBot.interface';

/**
 * 🎯 텍스트에서 [[봇이름]] 패턴 추출
 * @param text - 검색할 텍스트
 * @returns 발견된 봇 이름 배열 (중복 제거)
 */
export function parseBotTags(text: string): string[] {
  if (!text) return [];

  // [[봇이름]] 패턴 찾기
  const botPattern = /\[\[([^\]]+)\]\]/g;
  const matches = text.matchAll(botPattern);

  const botNames = Array.from(matches, (match) => match[1].trim());

  // 중복 제거
  return Array.from(new Set(botNames));
}

/**
 * 🎯 봇 실행 - 옵션 타입
 * @param bot - 실행할 봇 정보
 * @returns 랜덤으로 선택된 옵션
 */
function executeOptionBot(bot: ICustomBot): string {
  if (!bot.options || bot.options.length === 0) {
    return '옵션 없음';
  }

  const randomIndex = Math.floor(Math.random() * bot.options.length);
  return bot.options[randomIndex];
}

/**
 * 🎯 봇 실행 - 수식 타입
 * @param bot - 실행할 봇 정보
 * @returns 수식 실행 결과
 */
function executeFormulaBot(bot: ICustomBot): string | number {
  if (!bot.formula) {
    return '수식 없음';
  }

  try {
    // 🔒 보안: eval 대신 Function 사용 (더 안전)
    // 허용된 Math 함수만 사용 가능
    const allowedFunctions = {
      Math,
      floor: Math.floor,
      ceil: Math.ceil,
      round: Math.round,
      random: Math.random,
      abs: Math.abs,
      min: Math.min,
      max: Math.max,
    };

    // Function을 사용하여 수식 실행
    const func = new Function(
      ...Object.keys(allowedFunctions),
      `return ${bot.formula};`,
    );
    const result = func(...Object.values(allowedFunctions));

    return result;
  } catch (error) {
    console.error('수식 실행 오류:', error);
    return '실행 오류';
  }
}

/**
 * 🎯 봇 실행 메인 함수
 * @param bot - 실행할 봇 정보
 * @returns 봇 실행 결과
 */
export function executeBot(bot: ICustomBot): IBotExecutionResult {
  let result: string | number;

  switch (bot.type) {
    case CUSTOM_BOT_TYPE.OPTIONS:
      result = executeOptionBot(bot);
      break;

    case CUSTOM_BOT_TYPE.FORMULA:
      result = executeFormulaBot(bot);
      break;

    default:
      result = '알 수 없는 봇 타입';
  }

  return {
    botId: bot.id || '',
    botValue: bot.value,
    result,
    executedAt: new Date(),
    type: bot.type,
  };
}

/**
 * 🎯 텍스트에서 봇 태그를 찾아서 모두 실행하고 결과를 HTML로 추가
 * @param text - 원본 텍스트
 * @param bots - 등록된 봇 목록
 * @param appendResults - 결과를 텍스트 뒤에 HTML로 추가할지 여부 (기본: true)
 * @returns { processedText, results }
 */
export async function processBotTags(
  text: string,
  bots: ICustomBot[],
  appendResults: boolean = true,
): Promise<{
  processedText: string;
  results: { [botValue: string]: IBotExecutionResult };
}> {
  const botNames = parseBotTags(text);
  const results: { [botValue: string]: IBotExecutionResult } = {};

  let processedText = text;

  for (const botName of botNames) {
    // 봇 찾기 (활성화된 봇만)
    const bot = bots.find((b) => b.value === botName && b.isActive);

    if (bot) {
      // 봇 실행
      const executionResult = executeBot(bot);
      results[botName] = executionResult;
    }
  }

  // 🎯 결과를 HTML로 추가
  if (appendResults && Object.keys(results).length > 0) {
    const resultsHTML = generateBotResultsHTML(results);
    processedText = text + resultsHTML;
  }

  return {
    processedText,
    results,
  };
}

/**
 * 🎯 봇 실행 결과를 HTML로 생성
 * @param results - 봇 실행 결과 맵
 * @returns HTML 문자열
 */
function generateBotResultsHTML(results: {
  [botValue: string]: IBotExecutionResult;
}): string {
  const resultItems = Object.entries(results)
    .map(([botName, result]) => {
      // 타입에 따라 이모지 추가
      const icon = result.type === CUSTOM_BOT_TYPE.OPTIONS ? '🎲' : '🔢';

      // 🎯 공백 없이 한 줄로 생성 (white-space: pre-wrap 대응)
      return `<div style="display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; margin: 4px 4px 4px 0; background: var(--inkColor); border: 1px solid var(--lineColor); border-radius: 6px; font-size: 13px;"><span style="opacity: 0.7;">${icon} [[${botName}]]</span><span style="margin: 0 4px;">▸</span><strong style="color: var(--textColor); font-size: 14px;">${result.result}</strong></div>`;
    })
    .join('');

  // 🎯 컨테이너도 한 줄로 생성
  return `<div style="display: flex; flex-wrap: wrap;">${resultItems}</div>`;
}

/**
 * 🎯 봇 결과를 읽기 쉬운 텍스트로 변환
 * @param results - 봇 실행 결과 맵
 * @returns 포맷된 문자열
 */
export function formatBotResults(results: {
  [botValue: string]: IBotExecutionResult;
}): string {
  return Object.entries(results)
    .map(([botName, result]) => `[[${botName}]] → ${result.result}`)
    .join(' | ');
}

/**
 * 🎯 범용: 모든 게시판에서 사용 가능한 봇 처리 헬퍼
 * @param contents - 원본 콘텐츠
 * @param isUpdate - 수정 모드인지 (true면 봇 재실행 안 함)
 * @returns { contents: 원본 콘텐츠 그대로, botResults: 실행 결과 }
 */
export async function processContentWithBots(
  contents: string,
  isUpdate: boolean = false,
): Promise<{
  contents: string;
  botResults?: { [botValue: string]: IBotExecutionResult };
}> {
  // 🎯 수정 모드면 봇 실행 안 함 (기존 결과 유지)
  if (isUpdate) {
    return {
      contents,
      botResults: undefined,
    };
  }

  // 활성화된 봇 가져오기
  const activeBots = await getActiveBots();

  // 봇 실행 (contents에는 추가하지 않음)
  const { processedText, results } = await processBotTags(
    contents,
    activeBots,
    false, // HTML 결과를 contents에 추가하지 않음
  );

  // 봇 사용 횟수 증가
  for (const botResult of Object.values(results)) {
    if (botResult.botId) {
      await incrementBotUsage(botResult.botId);
    }
  }

  return {
    contents, // 원본 그대로
    botResults: Object.keys(results).length > 0 ? results : undefined,
  };
}
